import SockJS from 'sockjs-client';
import Stomp from 'stompjs';
import { getStorage } from '@/utils/storage';

type IClient = Stomp.Client;
type IFrame = Stomp.Frame;
type ISubscriber = Stomp.Subscription | null;

// 回调函数类型
type EmptyFn = () => void;
type IConnectBeforeCb = (reconnect: number, client: IClient) => void;
type IConnectedCb = (params?: IFrame | string) => void;
type IConnectErrorCb = (error: string) => void;
type ISubscribeMessageCb = (message: string) => void;

// 连接 Socket 的配置项
interface ISocketOptions {
  url: string;                    // socket 服务器地址
  retry?: number;                 // 最大重连次数. 默认不重连
  debug?: boolean;                // 是否开启 debug 模式. 默认关闭
  timeout?: number;               // 连接超时时间，以毫秒为单位. 默认不设置超时
  header?: object;                // 连接时携带的 header 参数
  onBefore?: IConnectBeforeCb;    // 连接之前的回调函数
  onConnected: IConnectedCb;      // 连接成功的回调函数
  onError?: IConnectErrorCb;      // 连接出错的回调函数
}

// 订阅 Socket 的配置项
interface ISubscribeOptions {
  url: string;                    // socket 订阅地址
  timeout?: number;               // 接收超时时间，以毫秒为单位，默认不设置超时
  client: IClient;                // socket 客户端
  onBefore?: EmptyFn;             // 开始订阅之前的回调函数
  onMessage: ISubscribeMessageCb; // 接收信息的回调函数
  onTimeout?: EmptyFn;            // 信息接收超时的回调函数
}

interface IConnectResult {
  status: 'success' | 'error' | 'timeout';
  data?: IFrame | string;
}

// 创建并连接 socket
function connect(options: ISocketOptions) {
  return createClient(options, 0);
}

// 断开连接 socket
function disconnect(client: IClient, callback?: () => void) {
  if (client && client.connected) {
    client.disconnect(() => {
      callback?.();
      // tslint:disable-next-line:no-console
      console.log('连接已断开');
    });
  }
}

// 创建 socket 客户端
function createClient(options: ISocketOptions, reconnect: number) {
  const socket = new SockJS(options.url);
  const client = Stomp.over(socket);
  const retry = options.retry || 0;
  const isMax = reconnect > (retry - 1);  // 是否已经达到最大重连次数

  // 连接失败
  function connectError(error: IFrame | string) {
    const errMsg = typeof error === 'string' ? error : error.body;
    options?.onError?.(errMsg);
  }

  // 关闭 debug 模式
  if (!options?.debug) {
    client.debug = () => void (0);
  }

  // 连接之前的回调
  options?.onBefore?.(reconnect, client);

  // 连接 socket
  startConnect(client, options).then((result) => {
    if (result.status === 'success') {
      options.onConnected(result.data);
    } else if (result.status === 'error') {
      // 连接出错时，进行重连
      isMax ? connectError(result.data!) : createClient(options, reconnect + 1);
    } else if (result.status === 'timeout') {
      // 连接超时后，进行重连
      isMax ? connectError('连接出错，请稍后再试') : createClient(options, reconnect + 1);
    }
  });

  return client;
}

// 开始连接 socket
function startConnect(client: IClient, options: ISocketOptions) {
  let timer: any;

  // Promise 状态确定后，无法更改，所以仅第一次 resole 生效, 后续的 resolve 或 reject 将被忽略
  return new Promise<IConnectResult>((resolve) => {
    const startTime = Date.now();
    const maxTimeout = options.timeout || 0;  // 最大超时时间
    const useTimeout = maxTimeout > 0;        // 是否使用超时机制. 默认不使用(一直等待下去，不做任何处理)
    const header = options.header || null;
    const token = getStorage('token');

    const resolveFn = (data: IConnectResult) => {
      const notTimeout = !useTimeout || (Date.now() - startTime < maxTimeout);
      if (notTimeout) {
        clearTimeout(timer);
        resolve(data);
      }
    };

    client.connect(
      { ...header, 'X-Authorization': token },
      (frame) => resolveFn({ status: 'success', data: frame }),
      (error) => resolveFn({ status: 'error', data: error }),
    );

    // 如果没设置超时，则不执行后面的超时逻辑
    if (maxTimeout === 0) {
      return;
    }

    // 连接超时(等待 `maxTimeout` 毫秒后，既没连接成功又没连接失败,则认为超时)
    timer = setTimeout(() => {
      clearTimeout(timer);
      resolve({ status: 'timeout' });
    }, maxTimeout);
  });
}

// 发送 socket 消息
function send(client: IClient, url: string, params: object | string, headers?: object) {
  const header = typeof headers === 'object' ? headers : {};
  const body = typeof params === 'string' ? params : (params ? JSON.stringify(params) : '');
  if (body) {
    client.send(url, header, body);
  }
}

// 订阅 socket 频道
function subscribe(options: ISubscribeOptions) {
  let timer: any = null;
  let shouldContinue = true;
  let subscription: ISubscriber = null;
  const maxTimeout = options.timeout || 0;
  const useTimeout = maxTimeout > 0;  // 是否使用超时机制. 默认不使用(一直等待下去，不做任何处理)
  const onTimeout = options?.onTimeout;

  // 模拟 setInterval, 防止前后两次回调间隔过长导致的重叠和累积误差
  function startInterval() {
    if (!shouldContinue) {
      return;
    }
    timer = setTimeout(() => {
      onTimeout?.();
      startInterval();
    }, maxTimeout);
  }

  // 取消订阅
  function unsubscribe() {
    shouldContinue = false;
    clearTimeout(timer);
    subscription?.unsubscribe();
  }

  // 订阅之前的回调函数
  options?.onBefore?.();

  // 订阅之前开始计时
  if (useTimeout) {
    startInterval();
  }

  // 开始订阅频道
  // 如果前后两条消息的时间间隔大于 `TIMEOUT`, 则每隔 `TIMEOUT` 毫秒都会执行一次 onTimeout 回调
  subscription = options.client.subscribe(options.url, (message) => {
    options.onMessage(message.body);
    if (useTimeout) {
      clearTimeout(timer);
      startInterval(); // 在接收到消息后重新设置定时任务(小于 `TIMEOUT` 时间间隔的定时任务不会被执行)
    }
  });

  return unsubscribe;
}

export {
  IClient,
  connect,
  disconnect,
  send,
  subscribe,
};
